/**
 * Resume Studio — RevisionCard + Revision Top-up Modal (V1.4.5 refactor).
 *
 * Split out of PreviewAndPay.jsx to keep that file under 500 lines.
 * RevisionCard is the tier-aware widget shown on the paid state to
 * consume/track AI revisions. TopupModal is the ₹599 upsell for +10 more.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import { fireGadsConversion } from "@/lib/gtagConversions";
import { loadRazorpay } from "./shared";

function RevisionCard({ session, onSessionChange }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [showTopupModal, setShowTopupModal] = useState(false);
    const used = session.revisions_used || 0;
    const max = session.revisions_max || 0;
    const remaining = Math.max(0, max - used);

    if (max <= 0) return null;

    const submit = async () => {
        if (!note || note.length < 3) return toast.error("Enter revision instructions");
        setBusy(true);
        try {
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/revise`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ note }),
            });
            const d = await r.json();
            if (!r.ok) {
                if (r.status === 402 && d.detail?.error === "revision_limit_reached") {
                    setShowTopupModal(true);
                    return;
                }
                throw new Error(typeof d.detail === "string" ? d.detail : d.detail?.message || "Revision failed");
            }
            onSessionChange({
                ...session,
                revisions_used: d.revisions_used,
                revisions_max: d.revisions_max,
                rewritten_ats: d.rewritten_ats,
            });
            setNote("");
            toast.success(`Revision applied · ${d.revisions_used}/${d.revisions_max} used`);
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold inline-flex items-center gap-1.5">
                        <ArrowsClockwise size={14} /> AI Revisions
                    </span>
                    <span className="text-xs text-slate-500">
                        {used}/{max} used
                    </span>
                </div>
                {remaining > 0 ? (
                    <>
                        <textarea
                            data-testid="revision-note-textarea"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            placeholder="e.g. Make the summary more concise, emphasize Python skills"
                            className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        />
                        <button
                            onClick={submit}
                            disabled={busy || note.length < 3}
                            data-testid="revision-submit-btn"
                            className="mt-2 w-full text-sm bg-slate-900 text-white font-semibold py-2 rounded-lg disabled:opacity-40 hover:bg-slate-700"
                        >
                            {busy ? "Re-writing…" : `Ask for revision (${remaining} left)`}
                        </button>
                    </>
                ) : (
                    <div className="mt-2 text-xs text-slate-500">
                        You've used all revisions.
                        <button
                            onClick={() => setShowTopupModal(true)}
                            data-testid="revision-topup-btn"
                            className="ml-1 text-cobalt-700 font-semibold underline"
                        >
                            Top up ₹599 for 10 more
                        </button>
                    </div>
                )}
            </div>
            {showTopupModal && (
                <TopupModal
                    session={session}
                    onClose={() => setShowTopupModal(false)}
                    onSessionChange={onSessionChange}
                />
            )}
        </>
    );
}

function TopupModal({ session, onClose, onSessionChange }) {
    const [busy, setBusy] = useState(false);

    const pay = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/resume-studio/session/${session.session_id}/order`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ kind: "topup" }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Order failed");
            await loadRazorpay();
            const rzp = new window.Razorpay({
                key: d.key_id,
                amount: d.amount_paise,
                currency: d.currency,
                name: "NaukriDaddy Resume Studio",
                description: "Revision top-up · 10 extra revisions",
                order_id: d.order_id,
                theme: { color: "#0b5bd6" },
                prefill: { email: session.email },
                handler: async (resp) => {
                    const v = await fetch(
                        `${API}/resume-studio/session/${session.session_id}/verify`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                razorpay_order_id: resp.razorpay_order_id,
                                razorpay_payment_id: resp.razorpay_payment_id,
                                razorpay_signature: resp.razorpay_signature,
                            }),
                        },
                    );
                    const vd = await v.json();
                    if (v.ok) {
                        onSessionChange(vd.session);
                        // Google Ads conversion event for the top-up purchase
                        fireGadsConversion("resume_studio_purchase", {
                            value: 599,
                            currency: "INR",
                            transaction_id: resp.razorpay_payment_id,
                        });
                        toast.success("+10 revisions added!");
                        onClose();
                    }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rzp.open();
        } catch (e) {
            toast.error(String(e.message).slice(0, 200));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="topup-modal"
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        >
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
                <h3 className="text-lg font-bold">Out of revisions</h3>
                <p className="text-sm text-slate-600 mt-1">
                    Get <span className="font-bold">10 more AI revisions</span> for just ₹599 — cheaper than
                    upgrading tiers.
                </p>
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={onClose}
                        data-testid="topup-cancel-btn"
                        className="flex-1 border border-slate-300 rounded-lg py-2 font-semibold hover:bg-slate-50"
                    >
                        Not now
                    </button>
                    <button
                        onClick={pay}
                        disabled={busy}
                        data-testid="topup-pay-btn"
                        className="flex-1 bg-cobalt-600 text-white rounded-lg py-2 font-semibold hover:bg-cobalt-700 disabled:opacity-40"
                    >
                        {busy ? "Opening…" : "Pay ₹599"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { RevisionCard, TopupModal };
export default RevisionCard;
